import React, { useState } from "react"
import {
  Upload,
  Button,
  message,
  Progress,
  Alert,
  Modal,
  Typography,
  Space,
} from "antd"
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons"

const { Dragger } = Upload
const { Title, Text, Paragraph } = Typography

const CsvUploadButton = ({ rubricId, onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showTemplates, setShowTemplates] = useState(false)
  const [errors, setErrors] = useState([])

  const handleUpload = (file) => {
    // Clear previous errors when user selects a new file
    setErrors([])

    // Validate file type
    const isExcelFile = file.type === "application/vnd.ms-excel"
      || file.type
        === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      || file.name.endsWith(".xls")
      || file.name.endsWith(".xlsx")
      || file.type === "text/csv"
      || file.name.endsWith(".csv")

    if (!isExcelFile) {
      message.error("Only Excel and CSV files are allowed!")
      return false
    }

    // Validate file size (10MB)
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      message.error("File must be smaller than 10MB!")
      return false
    }

    setUploading(true)
    setUploadProgress(0)

    const formData = new FormData()
    formData.append("file", file)

    APIClient.post(
      `/sustainability_rubrics/${rubricId}/upload_csv`,
      formData,
      (response) => {
        message.success(response.message || "Upload successful!")
        setErrors([])
        if (onUploadSuccess) {
          onUploadSuccess()
        }
        setUploading(false)
        setUploadProgress(0)
      },
      (errors) => {
        console.log("Raw error response:", errors)

        // Handle validation errors from the server
        if (errors && errors.errors && Array.isArray(errors.errors)) {
          setErrors(errors.errors)
          message.error(
            "CSV validation failed. Please fix the errors and try again.",
          )
        } else if (errors && Array.isArray(errors)) {
          setErrors(errors)
          message.error(
            "CSV validation failed. Please fix the errors and try again.",
          )
        } else {
          let errorMessage = "Upload failed. Please check your file format and try again."

          if (
            errors
            && errors.responseJSON
            && errors.responseJSON.errors
            && Array.isArray(errors.responseJSON.errors)
          ) {
            errorMessage = errors.responseJSON.errors.join(", ")
          } else if (errors && errors.responseText) {
            errorMessage = errors.responseText
          }

          setErrors([errorMessage])
          message.error(errorMessage)
        }

        setUploading(false)
        setUploadProgress(0)
      },
      {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setUploadProgress(progress)
        },
      },
    )

    return false // Prevent default upload behavior
  }

  // const uploadProps = {
  //   name: "file",
  //   multiple: false,
  //   accept: ".csv,.xls,.xlsx",
  //   beforeUpload: handleUpload,
  //   showUploadList: false,
  //   disabled: uploading,
  // }

  const downloadTemplate = (type) => {
    // Generate Excel template with dropdown functionality
    const templateUrl = `/api/v1/sustainability_rubrics/${rubricId}/generate_template?type=${type}`

    // Create a temporary link to download the file
    const link = document.createElement("a")
    link.href = templateUrl
    link.download = `sustainability_rubric_${type}_template.xls`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            message="Rubric Data Upload"
            description="Upload an Excel file to bulk update your Sustainability Rubric data. Two formats are available: Domain Summary or Indicator Details. Excel templates include dropdown validation for Score column."
            type="info"
            showIcon
            action={(
              <Button
                size="small"
                type="link"
                onClick={() => setShowTemplates(true)}
              >
                View Templates
              </Button>
            )}
          />

          <Dragger
            name="file"
            multiple={false}
            accept=".csv,.xls,.xlsx"
            beforeUpload={handleUpload}
            showUploadList={false}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {uploading
                ? "Uploading..."
                : "Click or drag Excel file to this area to upload"}
            </p>
            <p className="ant-upload-hint">
              Only Excel files are supported. Maximum file size: 10MB. Templates
              include dropdown validation for Score column.
            </p>
          </Dragger>

          {/* Error Messages Display */}
          {console.log("Errors:", errors)}
          {errors.length > 0 && !uploading && (
            <div style={{ marginTop: "16px" }}>
              <Alert
                message="CSV Validation Errors"
                description={(
                  <div>
                    {errors.map((error, index) => (
                      <div key={index} style={{ marginBottom: "4px" }}>
                        • {error}
                      </div>
                    ))}
                  </div>
                )}
                type="error"
                showIcon
                closable
                onClose={() => setErrors([])}
              />
            </div>
          )}

          {uploading && (
            <Progress
              percent={uploadProgress}
              status="active"
              strokeColor={{
                "0%": "#108ee9",
                "100%": "#87d068",
              }}
            />
          )}
        </Space>
      </div>

      <Modal
        title="CSV Templates"
        open={showTemplates}
        onCancel={() => setShowTemplates(false)}
        footer={[
          <Button key="close" onClick={() => setShowTemplates(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={4}>Format 1: Domain Summary</Title>
            <Paragraph>
              Use this format to update domain-level areas of strength and
              opportunities for growth.
            </Paragraph>
            <Text strong>Required columns:</Text> Domains, Areas of Strength,
            Opportunities for Growth
            <br />
            <Button
              icon={<DownloadOutlined />}
              onClick={() => downloadTemplate("domain_summary")}
              style={{ marginTop: 8 }}
            >
              Download Template
            </Button>
          </div>

          <div>
            <Title level={4}>Format 2: Indicator Details</Title>
            <Paragraph>
              Use this format to update individual indicator ratings and
              justifications.
            </Paragraph>
            <Text strong>Required columns:</Text> Indicator Number (1-27), Title
            (exact match), Score (Initiating/Developing/Proficient/well
            developed), Justification (any text)
            <br />
            <Button
              icon={<DownloadOutlined />}
              onClick={() => downloadTemplate("indicator_details")}
              style={{ marginTop: 8 }}
            >
              Download Template
            </Button>
          </div>
        </Space>
      </Modal>
    </>
  )
}

export default CsvUploadButton
